import React, { useEffect, useState } from "react";
import { api, fileUrl } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { Upload, X, Calendar as CalendarIcon } from "lucide-react";
import { format, parseISO } from "date-fns";
import { useI18n } from "../lib/i18n";

const HAIR_OPTIONS = ["black", "brown", "blond", "red", "grey", "white", "bald"];
const EYE_OPTIONS = ["brown", "blue", "green", "grey", "hazel", "black", "amber"];

const FIELDS_COMMON = {
  age_min: "", age_max: "", gender: "unknown", height_cm: "", weight_kg: "",
  hair_color: "", eye_color: "", ethnicity: "", distinguishing_marks: "",
  clothing: "", country: "", location: "", event_date: "", description: "",
  photo_path: "", date_of_birth: "", height_min: "", height_max: "",
};

function ageRangeAt(dob, onDate) {
  if (!dob) return null;
  const p = String(dob).split("-");
  if (!/^\d{4}$/.test(p[0] || "")) return null;
  const y = parseInt(p[0], 10);
  const m = p[1] ? parseInt(p[1], 10) : null;
  const d = p[2] ? parseInt(p[2], 10) : null;
  const ref = onDate ? new Date(onDate) : new Date();
  if (isNaN(ref.getTime())) return null;
  const n = ref.getFullYear() - y;
  const rm = ref.getMonth() + 1;
  let lo, hi;
  if (!m) { lo = n - 1; hi = n; }
  else if (!d) {
    if (rm > m) { lo = hi = n; }
    else if (rm < m) { lo = hi = n - 1; }
    else { lo = n - 1; hi = n; }
  } else {
    const a = n - ((rm < m || (rm === m && ref.getDate() < d)) ? 1 : 0);
    lo = hi = a;
  }
  lo = Math.max(lo, 0); hi = Math.max(hi, 0);
  return hi >= 0 && hi <= 130 ? [lo, hi] : null;
}

function fmtAgeRange(r) {
  if (!r) return "—";
  return r[0] === r[1] ? `${r[0]}` : `${r[0]}–${r[1]}`;
}

export default function CaseForm({ kind, onSubmit, caseId, initialData }) {
  const isAnte = kind === "ante_mortem";
  const isEdit = Boolean(caseId);
  const { t, lang } = useI18n();
  const [countries, setCountries] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(() => {
    const base = {
      ...FIELDS_COMMON,
      full_name: "",
      contact_relation: "",
      contact_name: "",
      contact_phone: "",
      contact_email: "",
      reference_code: "",
      estimated_pmi_days: "",
      case_notes: "",
    };
    if (initialData) {
      Object.keys(base).forEach((k) => {
        if (initialData[k] !== undefined && initialData[k] !== null) base[k] = initialData[k];
      });
    }
    return base;
  });
  const [error, setError] = useState("");
  const [invalid, setInvalid] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const computedAge = ageRangeAt(form.date_of_birth, form.event_date);

  useEffect(() => {
    api.get("/countries").then((r) => setCountries(r.data.countries)).catch(() => {});
  }, []);

  const clearInvalid = (k) =>
    setInvalid((iv) => { if (!iv[k]) return iv; const n = { ...iv }; delete n[k]; return n; });

  const set = (k) => (e) => {
    const val = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: val }));
    clearInvalid(k);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, photo_path: data.path }));
    } catch (err) {
      setError("Upload failed. " + (err.response?.data?.detail || ""));
    }
    setUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    // Client-side check: highlight any empty required fields before submitting.
    const requiredKeys = isAnte ? ["full_name", "country"] : ["country"];
    const missing = {};
    requiredKeys.forEach((k) => {
      if (!String(form[k] ?? "").trim()) missing[k] = true;
    });
    if (Object.keys(missing).length) {
      setInvalid(missing);
      setError(t("form.required_error"));
      const firstEl = document.querySelector(`[data-testid="form-${missing.full_name ? "full-name" : "country"}"]`);
      firstEl?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setSubmitting(true);
    const payload = { ...form };
    if (isAnte) payload.lang = lang;
    // numbers
    ["age_min", "age_max", "height_cm", "height_min", "height_max", "weight_kg", "estimated_pmi_days"].forEach((k) => {
      payload[k] = payload[k] === "" || payload[k] == null ? null : Number(payload[k]);
    });
    // ante-mortem: derive age range at disappearance from the (partial) date of birth
    if (isAnte && form.date_of_birth) {
      const r = ageRangeAt(form.date_of_birth, form.event_date);
      payload.age_min = r ? r[0] : null;
      payload.age_max = r ? r[1] : null;
    }
    // strip irrelevant fields
    if (isAnte) {
      delete payload.reference_code;
      delete payload.estimated_pmi_days;
      delete payload.case_notes;
    } else {
      delete payload.full_name;
      delete payload.contact_relation;
      delete payload.contact_name;
      delete payload.contact_phone;
      delete payload.contact_email;
    }
    Object.keys(payload).forEach((k) => {
      if (payload[k] === "") payload[k] = null;
    });

    try {
      const base = isAnte ? "/ante-mortem" : "/post-mortem";
      const { data } = isEdit
        ? await api.put(`${base}/${caseId}`, payload)
        : await api.post(base, payload);
      onSubmit?.(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      // Fallback: highlight required fields that are still empty.
      const miss = {};
      requiredKeys.forEach((k) => { if (!String(form[k] ?? "").trim()) miss[k] = true; });
      if (Object.keys(miss).length) setInvalid(miss);
      setError(typeof d === "string" ? d : t("form.required_error"));
    }
    setSubmitting(false);
  };

  return (
    <form onSubmit={submit} className="space-y-8 border-t-4 pt-6" style={{ borderTopColor: isAnte ? "#4A7C59" : "#1F3A5F" }} data-testid="case-form">
      <Section title={t("form.section.id")}>
        {isAnte ? (
          <Field label={t("form.full_name")} testid="form-full-name" invalid={invalid.full_name}>
            <Input required value={form.full_name} onChange={set("full_name")} className={`h-11 ${invalid.full_name ? "border-[#9E4747] focus-visible:ring-[#9E4747]" : ""}`} data-testid="form-full-name-input" />
          </Field>
        ) : (
          <Field label={t("form.ref_code")} testid="form-reference-code">
            <Input value={form.reference_code} onChange={set("reference_code")} className="h-11" data-testid="form-reference-code-input" />
          </Field>
        )}
        {isAnte && (
          <Field label={t("form.relation")} testid="form-relation">
            <Input value={form.contact_relation} onChange={set("contact_relation")} className="h-11" placeholder="e.g. Mother, Brother" data-testid="form-relation-input" />
          </Field>
        )}
        {isAnte && (
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label={t("form.contact_name")} testid="form-contact-name">
              <Input value={form.contact_name} onChange={set("contact_name")} className="h-11" data-testid="form-contact-name-input" />
            </Field>
            <Field label={t("form.contact_phone")} testid="form-contact-phone">
              <Input value={form.contact_phone} onChange={set("contact_phone")} className="h-11" placeholder="+32 ..." data-testid="form-contact-phone-input" />
            </Field>
            <Field label={t("form.contact_email")} testid="form-contact-email">
              <Input type="email" value={form.contact_email} onChange={set("contact_email")} className="h-11" data-testid="form-contact-email-input" />
            </Field>
          </div>
        )}
      </Section>

      <Section title={t("form.section.demo")}>
        <div className="grid gap-4 sm:grid-cols-3">
          {isAnte ? (
            <>
              <Field label={t("form.dob")} testid="form-dob">
                <PartialDobField
                  value={form.date_of_birth || ""}
                  onChange={set("date_of_birth")}
                  t={t}
                />
              </Field>
              {form.date_of_birth ? (
                <Field label={t("form.age_at_disappearance")} testid="form-age-computed">
                  <Input
                    readOnly
                    value={fmtAgeRange(computedAge)}
                    className="h-11 bg-[#F8F9FA] text-[#5C6873]"
                    data-testid="form-age-computed-input"
                  />
                </Field>
              ) : (
                <Field label={t("form.age")} testid="form-age">
                  <Input
                    type="number"
                    min={0}
                    max={120}
                    value={form.age_min}
                    onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, age_min: v, age_max: v })); }}
                    className="h-11"
                    data-testid="form-age-input"
                  />
                  <p className="mt-1 font-body text-xs text-[#6B7F82]">{t("form.age_unknown_dob")}</p>
                </Field>
              )}
            </>
          ) : (
            <>
              <Field label={t("form.age_min")} testid="form-age-min">
                <Input type="number" min={0} max={120} value={form.age_min} onChange={set("age_min")} className="h-11" data-testid="form-age-min-input" />
              </Field>
              <Field label={t("form.age_max")} testid="form-age-max">
                <Input type="number" min={0} max={120} value={form.age_max} onChange={set("age_max")} className="h-11" data-testid="form-age-max-input" />
              </Field>
            </>
          )}
          <Field label={t("form.gender")} testid="form-gender">
            <Select value={form.gender} onValueChange={(v) => setForm((f) => ({ ...f, gender: v }))}>
              <SelectTrigger className="h-11" data-testid="form-gender-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="male">{t("form.gender.male")}</SelectItem>
                <SelectItem value="female">{t("form.gender.female")}</SelectItem>
                <SelectItem value="unknown">{t("form.gender.unknown")}</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
      </Section>

      <Section title={t("form.section.physical")}>
        <div className="grid gap-4 sm:grid-cols-2">
          {isAnte ? (
            <Field label={t("form.height")} testid="form-height"><Input type="number" value={form.height_cm} onChange={set("height_cm")} className="h-11" data-testid="form-height-input" /></Field>
          ) : (
            <>
              <Field label={t("form.height_min")} testid="form-height-min"><Input type="number" value={form.height_min} onChange={set("height_min")} className="h-11" data-testid="form-height-min-input" /></Field>
              <Field label={t("form.height_max")} testid="form-height-max"><Input type="number" value={form.height_max} onChange={set("height_max")} className="h-11" data-testid="form-height-max-input" /></Field>
            </>
          )}
          <Field label={t("form.weight")} testid="form-weight"><Input type="number" value={form.weight_kg} onChange={set("weight_kg")} className="h-11" data-testid="form-weight-input" /></Field>
          <Field label={t("form.hair")} testid="form-hair">
            <Select value={form.hair_color || "none"} onValueChange={(v) => setForm((f) => ({ ...f, hair_color: v === "none" ? "" : v }))}>
              <SelectTrigger className="h-11" data-testid="form-hair-input"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                {HAIR_OPTIONS.map((o) => (
                  <SelectItem key={o} value={o} data-testid={`form-hair-${o}`}>{t("attr." + o)}</SelectItem>
                ))}
                {form.hair_color && !HAIR_OPTIONS.includes(form.hair_color) && (
                  <SelectItem value={form.hair_color}>{form.hair_color}</SelectItem>
                )}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("form.eye")} testid="form-eye">
            <Select value={form.eye_color || "none"} onValueChange={(v) => setForm((f) => ({ ...f, eye_color: v === "none" ? "" : v }))}>
              <SelectTrigger className="h-11" data-testid="form-eye-input"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                {EYE_OPTIONS.map((o) => (
                  <SelectItem key={o} value={o} data-testid={`form-eye-${o}`}>{t("attr." + o)}</SelectItem>
                ))}
                {form.eye_color && !EYE_OPTIONS.includes(form.eye_color) && (
                  <SelectItem value={form.eye_color}>{form.eye_color}</SelectItem>
                )}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("form.ethnicity")} testid="form-ethnicity"><Input value={form.ethnicity} onChange={set("ethnicity")} className="h-11" data-testid="form-ethnicity-input" /></Field>
        </div>
        <Field label={t("form.marks")} testid="form-marks">
          <Textarea value={form.distinguishing_marks} onChange={set("distinguishing_marks")} className="min-h-[80px]" data-testid="form-marks-input" />
        </Field>
        <Field label={t("form.clothing")} testid="form-clothing">
          <Textarea value={form.clothing} onChange={set("clothing")} className="min-h-[80px]" data-testid="form-clothing-input" />
        </Field>
      </Section>

      <Section title={isAnte ? t("form.section.last_seen") : t("form.section.found")}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("form.country")} testid="form-country" invalid={invalid.country}>
            <Select value={form.country} onValueChange={(v) => { setForm((f) => ({ ...f, country: v })); clearInvalid("country"); }}>
              <SelectTrigger className={`h-11 ${invalid.country ? "border-[#9E4747] focus:ring-[#9E4747]" : ""}`} data-testid="form-country-trigger">
                <SelectValue placeholder="Select a country" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {countries.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("form.location")} testid="form-location">
            <Input value={form.location} onChange={set("location")} className="h-11" data-testid="form-location-input" />
          </Field>
          <Field label={isAnte ? t("form.date_seen") : t("form.date_found")} testid="form-date">
            <DatePicker value={form.event_date || ""} onChange={set("event_date")} testid="form-date-input" />
          </Field>
          {!isAnte && (
            <Field label={t("form.pmi")} testid="form-pmi">
              <Input type="number" min={0} value={form.estimated_pmi_days} onChange={set("estimated_pmi_days")} className="h-11" data-testid="form-pmi-input" />
            </Field>
          )}
        </div>
        <Field label={isAnte ? t("form.description.ante") : t("form.description.post")} testid="form-description">
          <Textarea value={form.description} onChange={set("description")} className="min-h-[100px]" data-testid="form-description-input" />
        </Field>
        {!isAnte && (
          <Field label={t("form.case_notes")} testid="form-case-notes">
            <Textarea value={form.case_notes} onChange={set("case_notes")} className="min-h-[80px]" data-testid="form-case-notes-input" />
          </Field>
        )}
      </Section>

      <Section title={t("form.section.photo")}>
        <div className="flex flex-wrap items-start gap-4">
          {form.photo_path ? (
            <div className="relative">
              <img src={fileUrl(form.photo_path)} alt="" className="h-32 w-32 rounded-md border border-[#E2E8F0] object-cover" data-testid="form-photo-preview" />
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, photo_path: "" }))}
                className="absolute -right-2 -top-2 grid h-6 w-6 place-items-center rounded-full bg-[#9E4747] text-white shadow"
                data-testid="form-photo-remove"
              >
                <X size={12} />
              </button>
            </div>
          ) : (
            <label className="flex h-32 w-32 cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-dashed border-[#E2E8F0] bg-[#F8F9FA] text-[#5C6873] transition-base hover:border-[#2E3A46] hover:text-[#2E3A46]" data-testid="form-photo-upload-label">
              <Upload size={18} />
              <span className="font-body text-xs">{uploading ? t("form.uploading") : t("form.upload")}</span>
              <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="form-photo-input" />
            </label>
          )}
          <p className="max-w-md font-body text-xs text-[#5C6873]">
            {t("form.photo_hint")}
          </p>
        </div>
      </Section>

      {error && (
        <div className="rounded-md border border-[#9E4747]/30 bg-[#9E4747]/5 px-3 py-2 font-body text-sm text-[#9E4747]" data-testid="form-error">
          {error}
        </div>
      )}

      <div className="flex justify-end">
        <Button type="submit" disabled={submitting} className="h-11 bg-[#2E3A46] px-6 text-white hover:bg-[#1A2024]" data-testid="form-submit-btn">
          {submitting ? t("form.saving") : isEdit ? t("form.submit.save") : isAnte ? t("form.submit.ante") : t("form.submit.post")}
        </Button>
      </div>
    </form>
  );
}

function Section({ title, children }) {
  return (
    <section className="rounded-lg border border-[#E2E8F0] bg-white p-6 shadow-sm">
      <h3 className="font-heading text-sm font-semibold uppercase tracking-[0.16em] text-[#6B7F82]">{title}</h3>
      <div className="mt-5 space-y-4">{children}</div>
    </section>
  );
}

function Field({ label, children, testid, invalid }) {
  return (
    <div data-testid={testid}>
      <Label className={`font-body text-sm ${invalid ? "text-[#9E4747] font-medium" : "text-[#1A2024]"}`}>
        {label}{invalid ? " *" : ""}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function PartialDobField({ value, onChange, t }) {
  const init = String(value || "").split("-");
  const [yy, setYy] = useState(init[0] || "");
  const [mm, setMm] = useState(init[1] || "");
  const [dd, setDd] = useState(init[2] || "");
  const push = (ny, nm, nd) => {
    if (!/^\d{4}$/.test(ny)) { onChange(""); return; }
    let out = ny;
    const mi = parseInt(nm, 10);
    if (nm && mi >= 1 && mi <= 12) {
      out += `-${String(mi).padStart(2, "0")}`;
      const di = parseInt(nd, 10);
      if (nd && di >= 1 && di <= 31) out += `-${String(di).padStart(2, "0")}`;
    }
    onChange(out);
  };
  const handler = (which) => (e) => {
    const v = e.target.value.replace(/\D/g, "").slice(0, which === "y" ? 4 : 2);
    if (which === "d") { setDd(v); push(yy, mm, v); }
    else if (which === "m") { setMm(v); push(yy, v, dd); }
    else { setYy(v); push(v, mm, dd); }
  };
  return (
    <div data-testid="form-dob-partial">
      <div className="flex gap-2">
        <Input inputMode="numeric" value={dd} onChange={handler("d")} placeholder={t("form.dob_day")} className="h-11 w-16 text-center" data-testid="form-dob-day-input" />
        <Input inputMode="numeric" value={mm} onChange={handler("m")} placeholder={t("form.dob_month")} className="h-11 w-16 text-center" data-testid="form-dob-month-input" />
        <Input inputMode="numeric" value={yy} onChange={handler("y")} placeholder={t("form.dob_year")} className="h-11 w-24 text-center" data-testid="form-dob-year-input" />
      </div>
      <p className="mt-1 font-body text-xs text-[#6B7F82]">{t("form.dob_hint")}</p>
    </div>
  );
}

function DatePicker({ value, onChange, testid, placeholder }) {
  const [open, setOpen] = useState(false);
  const now = new Date();
  let selected;
  try {
    const d = value ? parseISO(value) : null;
    selected = d && !isNaN(d.getTime()) ? d : undefined;
  } catch {
    selected = undefined;
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className={`flex h-11 w-full items-center justify-between rounded-md border border-input bg-white px-3 font-body text-sm ${selected ? "text-[#1A2024]" : "text-[#8A96A0]"}`}
        >
          {selected ? format(selected, "dd/MM/yyyy") : (placeholder || "dd/mm/yyyy")}
          <CalendarIcon size={16} className="text-[#6B7F82]" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          captionLayout="dropdown-buttons"
          fromYear={1920}
          toYear={now.getFullYear()}
          selected={selected}
          defaultMonth={selected || new Date(1990, 0)}
          disabled={{ after: now }}
          onSelect={(d) => { onChange(d ? format(d, "yyyy-MM-dd") : ""); setOpen(false); }}
          initialFocus
          classNames={{
            caption: "flex justify-center pt-1 relative items-center",
            caption_dropdowns: "flex gap-1.5",
            dropdown: "rounded-md border border-[#E2E8F0] bg-white px-1.5 py-1 font-body text-sm text-[#1A2024] outline-none focus:border-[#4A7C59]",
            caption_label: "hidden",
            vhidden: "sr-only",
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
